/**
 * Core signal processing for remote photoplethysmography.
 *
 * Nothing here knows about heart rates or skin — it is windowing, detrending
 * and spectral estimation. The physiological layer sits in estimator.js.
 *
 * All functions are pure and allocation-conscious: the scan loop calls
 * `powerSpectrum` several times a second on a low-end phone.
 */

/** Remove the least-squares linear trend. Kills baseline wander and the DC term. */
export function detrend(x) {
  const n = x.length;
  if (n === 0) return new Float64Array(0);
  const meanIdx = (n - 1) / 2;
  let meanVal = 0;
  for (let i = 0; i < n; i++) meanVal += x[i];
  meanVal /= n;

  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    const d = i - meanIdx;
    num += d * (x[i] - meanVal);
    den += d * d;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanVal - slope * meanIdx;

  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = x[i] - (intercept + slope * i);
  return out;
}

const hannCache = new Map();

/** Periodic-symmetric Hann window, memoised by length. */
export function hannWindow(n) {
  let w = hannCache.get(n);
  if (w) return w;
  w = new Float64Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  if (hannCache.size < 32) hannCache.set(n, w);
  return w;
}

export function applyWindow(x, w) {
  const n = x.length;
  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = x[i] * w[i];
  return out;
}

export function nextPow2(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * In-place iterative radix-2 Cooley–Tukey FFT.
 * `re` and `im` must be the same power-of-two length.
 */
export function fft(re, im) {
  const n = re.length;
  if (n <= 1) return;
  if ((n & (n - 1)) !== 0) throw new Error(`fft: length ${n} is not a power of two`);

  // bit-reversal permutation
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wRe = Math.cos(ang);
    const wIm = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let curRe = 1;
      let curIm = 0;
      const half = len >> 1;
      for (let k = 0; k < half; k++) {
        const aRe = re[i + k];
        const aIm = im[i + k];
        const bRe = re[i + k + half] * curRe - im[i + k + half] * curIm;
        const bIm = re[i + k + half] * curIm + im[i + k + half] * curRe;
        re[i + k] = aRe + bRe;
        im[i + k] = aIm + bIm;
        re[i + k + half] = aRe - bRe;
        im[i + k + half] = aIm - bIm;
        const nextRe = curRe * wRe - curIm * wIm;
        curIm = curRe * wIm + curIm * wRe;
        curRe = nextRe;
      }
    }
  }
}

/**
 * Power spectrum of a real signal.
 *
 * `padFactor` zero-pads before the transform. Padding does not create
 * resolution, but it interpolates the spectrum onto a finer grid, which makes
 * peak location and prominence far less sensitive to where the true frequency
 * falls between bins. Every threshold in estimator.js is expressed in Hz
 * rather than bins so it stays meaningful whatever the padding.
 *
 * @returns {{power: Float64Array, df: number, fs: number, size: number}}
 *          power[k] corresponds to frequency k * df, for k < size/2.
 */
export function powerSpectrum(samples, { fs = 30, padFactor = 4, detrended = false } = {}) {
  const x = detrended ? samples : detrend(samples);
  const windowed = applyWindow(x, hannWindow(x.length));

  const size = nextPow2(x.length * padFactor);
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  re.set(windowed);
  fft(re, im);

  const half = size >> 1;
  const power = new Float64Array(half);
  for (let k = 0; k < half; k++) power[k] = re[k] * re[k] + im[k] * im[k];

  return { power, df: fs / size, fs, size };
}

/** Inclusive bin index range covering [lo, hi] Hz. */
export function bandRange(spec, lo, hi) {
  const first = Math.max(1, Math.ceil(lo / spec.df));
  const last = Math.min(spec.power.length - 2, Math.floor(hi / spec.df));
  return { first, last };
}

/**
 * Locate the dominant peak inside a frequency band and describe how much it
 * stands out.
 *
 * `prominence` is the ratio of the peak's power to the strongest competing
 * power outside an exclusion zone of ±`excludeHz`. It is the honest measure of
 * "is there really a periodic thing here" — unlike a plain SNR, broadband noise
 * cannot inflate it, because the competitor is also drawn from that noise.
 *
 * @returns {{freq, power, prominence, snr, index}|null}
 */
export function bandPeak(spec, lo, hi, { excludeHz = 0.25 } = {}) {
  const { first, last } = bandRange(spec, lo, hi);
  if (last <= first) return null;

  const p = spec.power;
  let best = first;
  for (let k = first; k <= last; k++) if (p[k] > p[best]) best = k;

  // Sub-bin refinement by parabolic interpolation on the log-magnitude peak.
  let freq = best * spec.df;
  const a = p[best - 1];
  const b = p[best];
  const c = p[best + 1];
  const den = a - 2 * b + c;
  if (den !== 0) {
    const delta = (a - c) / (2 * den);
    if (delta > -1 && delta < 1) freq = (best + delta) * spec.df;
  }

  const exclude = Math.max(1, Math.round(excludeHz / spec.df));
  let competitor = 0;
  let mean = 0;
  let count = 0;
  const band = new Float64Array(last - first + 1);
  for (let k = first; k <= last; k++) {
    mean += p[k];
    band[count] = p[k];
    count++;
    if (Math.abs(k - best) > exclude && p[k] > competitor) competitor = p[k];
  }
  mean = count ? mean / count : 1;

  // Median is the honest noise-floor estimate: the mean is inflated by the very
  // peak we are trying to judge, and by any strong artefact.
  band.sort();
  const median = count ? band[count >> 1] : 1;

  return {
    freq,
    power: p[best],
    prominence: competitor > 0 ? p[best] / competitor : Number.POSITIVE_INFINITY,
    snr: mean > 0 ? p[best] / mean : Number.POSITIVE_INFINITY,
    bandMedian: median,
    index: best,
  };
}

/** Power at an arbitrary frequency, linearly interpolated between bins. */
export function powerAt(spec, freq) {
  const x = freq / spec.df;
  const k = Math.floor(x);
  if (k < 0 || k + 1 >= spec.power.length) return 0;
  const frac = x - k;
  return spec.power[k] * (1 - frac) + spec.power[k + 1] * frac;
}

/**
 * How strongly the second harmonic of `freq` stands above the noise floor.
 *
 * A cardiac pulse is not a sine wave — the systolic upstroke and dicrotic notch
 * put real energy at 2f. Broadband noise and motion artefacts do not have that
 * structure, so requiring a harmonic is a cheap and very effective way to tell
 * a pulse from a coincidence. This is what lets the lock threshold stay low
 * enough to keep good readings while still refusing bad ones.
 */
export function harmonicSupport(spec, freq, noiseFloor) {
  if (!noiseFloor) return 0;
  const second = powerAt(spec, freq * 2);
  return second / noiseFloor;
}

/**
 * Welch-style averaged periodogram: split into half-overlapped segments and
 * average their power. Trades frequency resolution for variance reduction —
 * worth it only when the buffer is long relative to the segment.
 */
export function welchSpectrum(samples, { fs = 30, segment = 512, padFactor = 4 } = {}) {
  const step = segment >> 1;
  if (samples.length < segment) return powerSpectrum(samples, { fs, padFactor });

  let acc = null;
  let df = 0;
  let size = 0;
  let segments = 0;
  for (let i = 0; i + segment <= samples.length; i += step) {
    const spec = powerSpectrum(samples.subarray ? samples.subarray(i, i + segment) : samples.slice(i, i + segment), { fs, padFactor });
    if (!acc) {
      acc = Float64Array.from(spec.power);
      df = spec.df;
      size = spec.size;
    } else {
      for (let k = 0; k < acc.length; k++) acc[k] += spec.power[k];
    }
    segments++;
  }
  for (let k = 0; k < acc.length; k++) acc[k] /= segments;
  return { power: acc, df, fs, size, segments };
}

/** Peak-to-peak amplitude of the pulsatile component over the mean level. */
export function perfusionIndex(samples) {
  let dc = 0;
  for (let i = 0; i < samples.length; i++) dc += samples[i];
  dc /= samples.length;
  if (!dc) return 0;
  const d = detrend(samples);
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < d.length; i++) {
    if (d[i] < min) min = d[i];
    if (d[i] > max) max = d[i];
  }
  return (max - min) / dc;
}
